/**
 * Clinic CRUD — all operations scoped to organizations/{orgId}/clinics.
 */

import { addDoc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore'
import type { Clinic } from '../types'
import type { DayKey } from '../../shared/operatingDays'
import { clinicsCollection, clinicRef } from './firestore'
import { logStaffEdit } from './audit'

export interface ClinicFields {
  name: string
  street: string
  city: string
  state: string
  zip: string
  phone: string
  tin: string
  /** Legal business name — required (form-validated); used as the org name for Medicare checks. */
  legalName: string
  /** Group (Type-2) NPI for Medicare — optional ('' when the clinic doesn't serve Medicare). */
  groupNpi: string
  email: string
  /** Days the clinic sees patients (#133) — form requires at least one. */
  operatingDays: DayKey[]
}

/**
 * Returns a display string for a clinic's address, with fallback to the
 * legacy `address` field for docs written before the field split.
 */
export function formatClinicAddress(clinic: Clinic): string {
  if (clinic.city && clinic.state) {
    const parts = [clinic.street, `${clinic.city}, ${clinic.state}`, clinic.zip].filter(Boolean)
    return parts.join(', ')
  }
  return clinic.address ?? ''
}

/** Fetch a single clinic by ID. Returns null if not found. */
export async function getClinic(orgId: string, clinicId: string): Promise<Clinic | null> {
  const snap = await getDoc(clinicRef(orgId, clinicId))
  return snap.exists() ? snap.data() : null
}

/** List active clinics for an organization. */
export async function listClinics(orgId: string): Promise<Clinic[]> {
  const q = query(clinicsCollection(orgId), where('status', '==', 'active'))
  const snap = await getDocs(q)
  return snap.docs.map(d => d.data())
}

/** Create a new clinic. Returns the generated Firestore ID. */
export async function addClinic(
  orgId: string,
  fields: ClinicFields,
  uid: string,
): Promise<string> {
  const ref = await addDoc(clinicsCollection(orgId), {
    id: '',
    orgId,
    name: fields.name,
    street: fields.street,
    city: fields.city,
    state: fields.state,
    zip: fields.zip,
    phone: fields.phone,
    tin: fields.tin,
    legalName: fields.legalName,
    groupNpi: fields.groupNpi,
    email: fields.email,
    operatingDays: fields.operatingDays,
    status: 'active',
    createdAt: new Date().toISOString(),
    createdBy: uid,
  } as Clinic)
  logStaffEdit('clinics', ref.id, 'create')
  return ref.id
}

/** Partial update of a clinic document. */
export async function updateClinic(
  orgId: string,
  clinicId: string,
  fields: ClinicFields,
): Promise<void> {
  await updateDoc(clinicRef(orgId, clinicId), {
    name: fields.name,
    street: fields.street,
    city: fields.city,
    state: fields.state,
    zip: fields.zip,
    phone: fields.phone,
    tin: fields.tin,
    legalName: fields.legalName,
    groupNpi: fields.groupNpi,
    email: fields.email,
    operatingDays: fields.operatingDays,
  })
  logStaffEdit('clinics', clinicId, 'update')
}

/**
 * Archive a clinic (soft delete).
 *
 * Nothing else needs re-pointing: an admin's working clinic is resolved from
 * the live active-clinic list at render time (`utils/workingClinic.ts`), so a
 * clinic going archived just drops out of that list on the next load. This
 * deliberately replaced an org-level `defaultClinicId` pointer that archiving
 * did NOT maintain — which locked every admin in the org out of clinic-scoped
 * actions with "Clinic is not active." until the field was hand-repaired.
 */
export async function archiveClinic(orgId: string, clinicId: string): Promise<void> {
  await updateDoc(clinicRef(orgId, clinicId), { status: 'archived' })
  logStaffEdit('clinics', clinicId, 'update')
}
